// Damage Fund entry page.
//   Driver           → their OWN ledger (balance hero + transactions).
//   Office / admin    → company roll-up mirroring the spreadsheet LEDGER,
//                       with drill-in links + Run Deduction batch.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatMoney, formatMoneySigned } from '../utils/money';
import DamageFundLedger from './DamageFundLedger';
import DamageFundImportModal from './DamageFundImportModal';

export default function DamageFundPage() {
  const { isSuperAdmin, can } = useAuth();
  const canViewAll = isSuperAdmin || can('view_all_damage_funds');

  // Admin-only until the driver-facing rollout. Re-enable DriverOwnView
  // (and the Dispatch Hub card + driver dashboard tile) when drivers go live.
  if (canViewAll) return <AdminCompanyView />;
  return <Navigate to="/" replace />;
}

// ---- Driver's own ledger (unwired until driver rollout) -------
// eslint-disable-next-line no-unused-vars
function DriverOwnView() {
  const [account, setAccount] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { account } = await api.getMyDamageFund();
      setAccount(account);
      if (account) {
        const { transactions } = await api.getDamageFundTransactions(account.id);
        setTxns(transactions);
      }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleExport() {
    if (!account) return;
    const blob = await api.exportDamageFund({ account_id: account.id });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `my-damage-fund.xlsx`; a.click();
    window.URL.revokeObjectURL(url);
  }

  if (loading) return <div className="df-page">Loading…</div>;
  if (err) return <div className="df-page" style={{ color: '#ef4444' }}>{err}</div>;
  if (!account) {
    return (
      <div className="df-page">
        <h1>Damage Fund</h1>
        <p style={{ color: '#64748b' }}>You don't have a damage fund account yet. Check with the office.</p>
      </div>
    );
  }
  return (
    <div className="df-page">
      <h1 style={{ marginBottom: 16 }}>My Damage Fund</h1>
      <DamageFundLedger account={account} transactions={txns} onExport={handleExport} />
    </div>
  );
}

// ---- Admin company roll-up -----------------------------------
function AdminCompanyView() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSummary(await api.getDamageFundSummary()); }
    catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleExport() {
    const blob = await api.exportDamageFund();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'damage-fund-company.xlsx'; a.click();
    window.URL.revokeObjectURL(url);
  }

  if (loading) return <div className="df-page">Loading…</div>;
  if (err) return <div className="df-page" style={{ color: '#ef4444' }}>{err}</div>;

  const cards = [
    { label: 'Total Escrow Balance', value: formatMoney(summary.total_balance), color: '#0f172a' },
    { label: 'NC Credits (lifetime)', value: formatMoney(summary.total_nc_credit), color: '#10b981' },
    { label: 'Drivers', value: summary.driver_count, color: '#3b82f6' },
    { label: 'Underwater', value: summary.underwater_count, color: '#ef4444' },
    { label: 'At Cap', value: summary.at_cap_count, color: '#f59e0b' },
  ];

  return (
    <div className="df-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Damage Fund</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/damage-fund/deduction-batch')}>
            Run Pay-Period Deduction
          </button>
          <button type="button" className="btn btn-muted" onClick={() => setShowImport(true)}>Import from Spreadsheet</button>
          <button type="button" className="btn btn-gold" onClick={handleExport}>Export Excel</button>
        </div>
      </div>

      {showImport && (
        <DamageFundImportModal
          onClose={() => setShowImport(false)}
          onImported={load}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, margin: '20px 0' }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {summary.unmapped_count > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
          {summary.unmapped_count} account(s) are not yet mapped to a driver login. Map them so drivers can see their own balance.
        </div>
      )}

      {summary.groups.map(group => (
        <div key={group.group} style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569' }}>{group.group}</h3>
            <span style={{ fontWeight: 700, color: group.balance < 0 ? '#ef4444' : '#10b981' }}>{formatMoneySigned(group.balance)}</span>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Mapped User</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th style={{ textAlign: 'right' }}>NC Credits</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {group.accounts.map(a => (
                  <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/damage-fund/driver/${a.id}`)}>
                    <td style={{ fontWeight: 600 }}>{a.display_name}</td>
                    <td style={{ fontSize: 12, color: a.user_id ? '#64748b' : '#f59e0b' }}>
                      {a.user_full_name || '⚠ unmapped'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: a.balance < 0 ? '#ef4444' : '#10b981' }}>{formatMoneySigned(a.balance)}</td>
                    <td style={{ textAlign: 'right', color: '#64748b' }}>{formatMoney(a.nc_credit_total)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {a.at_cap ? <Badge color="#f59e0b">At Cap</Badge> : a.underwater ? <Badge color="#ef4444">Underwater</Badge> : <Badge color="#10b981">OK</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function Badge({ color, children }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: `${color}15`, color, textTransform: 'uppercase' }}>{children}</span>
  );
}
